import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import DynamicFormField from "./DynamicFormField";
import { journeyActions } from "../../store/journeySlice";
import { validateForm } from "../utils";
import { calculateAge, isDobWithinAgeRange } from "../utils/formatters";
import AttentionModal from "./AttentionModal";
import attentionImg from "../../assets/attentionimg.png";
import {
  findAnnualIncomeFieldName,
  getAnnualIncomeError,
} from "../utils/annualIncomeValidation";
import {
  GENDER_RESTRICTED_AGE_ERROR,
  violatesGenderRestrictedAge,
} from "../utils/genderAgeRestriction";
const ANNUITANT_MIN_AGE = 30;
const ANNUITANT_MAX_AGE = 75;
const ANNUITANT_AGE_ERROR = `Minimum age at entry of Primary Annuitant should be 30 years. Please select other member, or increase the age to proceed.`;
const INDIA_RESIDENT_FIELDS = [
  "basedInIndia",
  "nationality",
  "nriResident",
  "isIndiaResident",
  "areYouBasedInIndia",
];
const isIndiaResidentField = (name) => INDIA_RESIDENT_FIELDS.includes(name);
const isNoValue = (v) =>
  v &&
  ["no", "false", "nri", "non-resident"].includes(
    String(v).toLowerCase().trim(),
  );

const EditStepModal = ({
  isOpen,
  onClose,
  stepIndex = null,
  title = "Edit Details",
  onSave,
  className,
  formData,
  disabledFieldNames = [],
  disableAllFields = false,
}) => {
  const dispatch = useDispatch();
  const { formDefinition } = useSelector((s) => s.journey);
  const [editData, setEditData] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAttentionModal, setShowAttentionModal] = useState(false);
  const attentionModalRevertRef = useRef(null);
  const initialEditDataRef = useRef({});
  const modalRef = useRef(null);



  const resolveFieldValue = useCallback((fieldName) => {
    if (!fieldName || !formData) return "";
    if (formData[fieldName] !== undefined && formData[fieldName] !== null) {
      return formData[fieldName];
    }

    const aliasMap = {
      fullName: ["lifeAssuredName", "name", "proposerName"],
      lifeAssuredName: ["fullName", "name", "proposerName"],
      dateOfBirth: ["lifeAssuredDob", "dob", "proposerDob"],
      lifeAssuredDob: ["dateOfBirth", "dob", "proposerDob"],
      mobileNumber: ["mobile", "mobileNo", "liMobileno"],
      emailAddress: ["emailId", "email", "liEmailid"],
      residencyPinCode: ["pinCode", "pincode"],
      pinCode: ["residencyPinCode", "pincode"],
      gender: ["lifeAssuredGender", "proposerGender", "liGender"],
      lifeAssuredGender: ["gender", "proposerGender", "liGender"],
      occupation: ["occupationClass"],
      educationalLevel: ["education"],
      tobaccoConsumption: ["smokerStatus", "smokingHabit", "liSmoke"],
    };

    const aliases = aliasMap[fieldName] || [];
    for (const alias of aliases) {
      if (formData[alias] !== undefined && formData[alias] !== null) {
        return formData[alias];
      }
    }
    return "";
  }, [formData]);

  useEffect(() => {
    if (!isOpen || !formDefinition?.fieldGroups) return;
    const normalizeToken = (v) => String(v || "").toLowerCase().trim();

    const currentGroup =
      stepIndex !== null ? formDefinition.fieldGroups[stepIndex] : null;
    const stepFields =
      stepIndex !== null
        ? currentGroup?.fields || []
        : formDefinition.fieldGroups.flatMap((g) => g.fields || []);

    const stepData = {};
    stepFields.forEach((field) => {
      const fieldName = field.fieldReference || field.name || field.fieldName;
      stepData[fieldName] = resolveFieldValue(fieldName);
    });
    const isAnnuitant =
      normalizeToken(
        currentGroup?.fieldReference || currentGroup?.name || currentGroup?.title,
      ) === ("annuitant" || "lifeassured");
    if (isAnnuitant) {
      const relationshipField = stepFields.find((field) =>
        normalizeToken(
          field?.fieldReference || field?.name || field?.fieldName,
        ).includes(
          "relationship",
        ),
      );
      const relationshipFieldName =
        relationshipField?.fieldReference ||
        relationshipField?.name ||
        relationshipField?.fieldName;
      if (relationshipFieldName && !String(stepData[relationshipFieldName] || "").trim()) {
        stepData[relationshipFieldName] = "self";
      }
    }

    setEditData(stepData);
    initialEditDataRef.current = stepData;
    setErrors({});
  }, [isOpen, stepIndex, formDefinition, resolveFieldValue]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  if (!isOpen || !formDefinition?.fieldGroups) return null;

  const fields =
    stepIndex !== null
      ? formDefinition.fieldGroups[stepIndex]?.fields || []
      : formDefinition.fieldGroups.flatMap((g) => g.fields || []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (!name) return;
    const willShowAttention = isIndiaResidentField(name) && isNoValue(value);
    if (willShowAttention) {
      attentionModalRevertRef.current = {
        fieldName: name,
        previousValue: editData?.[name],
      };
    }
    setEditData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    if (willShowAttention) {
      setShowAttentionModal(true);
    }
  };

  const dismissAttentionModal = () => {
    const rev = attentionModalRevertRef.current;
    if (rev) {
      const { fieldName, previousValue } = rev;
      setEditData((prev) => ({ ...prev, [fieldName]: previousValue }));
      setErrors((prevE) => {
        const next = { ...prevE };
        delete next[fieldName];
        return next;
      });
      attentionModalRevertRef.current = null;
    }
    setShowAttentionModal(false);
  };

  const confirmAttentionModal = () => {
    attentionModalRevertRef.current = null;
    setShowAttentionModal(false);
  };

  const handleSave = async () => {
    const fieldErrors = validateForm(editData, filteredFields);
   
    const annualIncomeFieldName = findAnnualIncomeFieldName(filteredFields);
    if (annualIncomeFieldName && !fieldErrors[annualIncomeFieldName]) {
      const annualIncomeError = getAnnualIncomeError(
        editData?.[annualIncomeFieldName],
      );
      if (annualIncomeError) {
        fieldErrors[annualIncomeFieldName] = annualIncomeError;
      }
    }
    const annuitantAgeError = getAnnuitantAgeError();
    if (annuitantAgeError?.fieldName) {
      fieldErrors[annuitantAgeError.fieldName] = annuitantAgeError.message;
    }
    const genderAgeError = getGenderRestrictedAgeError();
    if (genderAgeError?.fieldName && !fieldErrors[genderAgeError.fieldName]) {
      fieldErrors[genderAgeError.fieldName] = genderAgeError.message;
    }
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    const normalizedEditData = syncAnnuitantSelfFields(editData);

    const hasChanges =
      JSON.stringify(normalizedEditData || {}) !==
      JSON.stringify(initialEditDataRef.current || {});

    dispatch(journeyActions.setLeadFormData(normalizedEditData));
    if (stepIndex !== null) {
      dispatch(
        journeyActions.saveStepData({ step: stepIndex, data: normalizedEditData }),
      );
    }
    if (typeof onSave === "function") {
      setSaving(true);
      try {
        const result = await onSave(normalizedEditData, { hasChanges, stepIndex });
        if (result === false) return;
      } finally {
        setSaving(false);
      }
    }
    onClose();
  };
  const disabledFieldNameSet = new Set(disabledFieldNames || []);

  const normalize = (v) => String(v || "").toLowerCase().trim();
  const resolveCurrentFieldValue = (fieldName) => {
    if (!fieldName) return "";
    if (editData?.[fieldName] !== undefined && editData?.[fieldName] !== null) {
      return editData[fieldName];
    }
    return resolveFieldValue(fieldName);
  };

  const isAnnuitantStep = (group) => {
    const token = normalize(group?.fieldReference || group?.name || group?.title);
    return token === "annuitant" || token === "leadannuitant" || token === "lifeassured";
  };

  const getFieldToken = (field) =>
    normalize(field?.fieldReference || field?.name || field?.fieldName);

  const isGenderField = (field) => getFieldToken(field).includes("gender");
  const isSinglePayField = (field) => getFieldToken(field) === "issinglepay";
  const isEnableSinglePayConditionField = (field) =>
    getFieldToken(field) === "enablesinglepaycondition";
  const isNameField = (field) => getFieldToken(field).includes("name");

  const isDobField = (field) => {
    const token = getFieldToken(field);
    return token.includes("dateofbirth") || token.includes("dob");
  };

  const currentStepGroup =
    stepIndex !== null ? formDefinition.fieldGroups[stepIndex] : null;
  const relationshipField =
    (currentStepGroup?.fields || []).find((field) =>
      getFieldToken(field).includes("relationship"),
    ) ||
    fields.find((field) => getFieldToken(field).includes("relationship"));
  const relationshipFieldName =
    relationshipField?.fieldReference ||
    relationshipField?.name ||
    relationshipField?.fieldName;
  const resolveSelfReference = () => {
    const options = Array.isArray(relationshipField?.options)
      ? relationshipField.options
      : [];
    const selfOption = options.find((opt) => {
      const label = normalize(opt?.label?.en_US ?? opt?.label);
      return label === "self";
    });
    return selfOption?.reference ? String(selfOption.reference).trim() : null;
  };
  const resolveRelationshipReference = (rawValue) => {
    if (rawValue == null) return rawValue;
    const normalizedRaw = normalize(rawValue);
    const options = Array.isArray(relationshipField?.options)
      ? relationshipField.options
      : [];
    const matched = options.find((opt) => {
      const reference = normalize(opt?.reference);
      const value = normalize(opt?.value);
      const label = normalize(opt?.label?.en_US ?? opt?.label);
      return (
        normalizedRaw === reference ||
        normalizedRaw === value ||
        normalizedRaw === label
      );
    });
    return matched?.reference ?? rawValue;
  };
  const relationshipValue = resolveRelationshipReference(
    relationshipFieldName ? editData?.[relationshipFieldName] : undefined,
  );
  const selfReference = resolveSelfReference();
  const isSelfRelationshipSelected = (value) => {
    const normalizedValue = normalize(value);
    return (
      normalizedValue === "self" ||
      (selfReference && String(value ?? "").trim() === selfReference)
    );
  };
  const resolveSinglePayThreshold = () => {
    const allFields = formDefinition?.fieldGroups?.flatMap((g) => g?.fields || []) || [];
    const conditionField = allFields.find((field) =>
      isEnableSinglePayConditionField(field),
    );
    if (!conditionField) return null;
    const key =
      conditionField?.fieldReference ||
      conditionField?.name ||
      conditionField?.fieldName;
    const fromData = key ? editData?.[key] : undefined;
    const fromDefault =
      conditionField?.defaultValue ??
      conditionField?.predefinedValue?.en_US ??
      conditionField?.predefinedValue;
    const threshold = Number(fromData ?? fromDefault);
    return Number.isFinite(threshold) ? threshold : null;
  };

  const resolveAnnuitantDobForSinglePayEligibility = () => {
    const annuitantGroup = isAnnuitantStep(currentStepGroup)
      ? currentStepGroup
      : formDefinition?.fieldGroups?.find((g) => isAnnuitantStep(g));
    if (!annuitantGroup) {
      return (
        resolveCurrentFieldValue("dateOfBirth") ||
        resolveCurrentFieldValue("lifeAssuredDob") ||
        resolveCurrentFieldValue("proposerDob") ||
        ""
      );
    }
    const annuitantRelationshipField = (annuitantGroup?.fields || []).find((field) =>
      getFieldToken(field).includes("relationship"),
    );
    const annuitantRelationshipFieldName =
      annuitantRelationshipField?.fieldReference ||
      annuitantRelationshipField?.name ||
      annuitantRelationshipField?.fieldName;
    const annuitantDobField = (annuitantGroup?.fields || []).find((field) =>
      isDobField(field),
    );
    const annuitantDobFieldName =
      annuitantDobField?.fieldReference ||
      annuitantDobField?.name ||
      annuitantDobField?.fieldName;
    const annuitantRelationshipValue = resolveRelationshipReference(
      annuitantRelationshipFieldName
        ? editData?.[annuitantRelationshipFieldName]
        : relationshipValue,
    );
    const isSelfSelected = isSelfRelationshipSelected(annuitantRelationshipValue);
    if (isSelfSelected) {
      return (
        resolveCurrentFieldValue("dateOfBirth") ||
        resolveCurrentFieldValue("lifeAssuredDob") ||
        resolveCurrentFieldValue("proposerDob") ||
        ""
      );
    }
    return annuitantDobFieldName
      ? editData?.[annuitantDobFieldName]
      : editData?.annuitantDateOfBirth || "";
  };

  const shouldShowSinglePayField = () => {
    const threshold = resolveSinglePayThreshold();
    if (!Number.isFinite(threshold)) return false;
    const annuitantDob = resolveAnnuitantDobForSinglePayEligibility();
    const age = calculateAge(annuitantDob);
    return age !== null && age >= threshold;
  };


  const isFieldVisible = (field) => {
    const fieldToken = getFieldToken(field);
    if (isEnableSinglePayConditionField(field)) return false;
    if (isSinglePayField(field)) return shouldShowSinglePayField();
    const fieldLooksAnnuitantScoped =
      fieldToken.includes("annuitant") || fieldToken.includes("lifeassured") ||
      fieldToken.includes("relationship") ||
      fieldToken.includes("dateofbirth") ||
      fieldToken.includes("dob") ||
      fieldToken.includes("gender");

    const isAnnuitantContext = currentStepGroup
      ? isAnnuitantStep(currentStepGroup) || fieldLooksAnnuitantScoped
      : fieldLooksAnnuitantScoped;

    if (!isAnnuitantContext) return true;

    const initialState = !relationshipValue;
    const selfSelected = isSelfRelationshipSelected(relationshipValue);

    if (initialState || selfSelected) {
      const hideForAnnuitantStep =
        currentStepGroup && isAnnuitantStep(currentStepGroup) &&
        (isGenderField(field) || isDobField(field) || isNameField(field));

      const hideForMixedContext =
        (!currentStepGroup || !isAnnuitantStep(currentStepGroup)) &&
        (fieldToken.includes("annuitant") || fieldToken.includes("lifeassured")) &&
        (isGenderField(field) || isDobField(field) || isNameField(field));

      if (hideForAnnuitantStep || hideForMixedContext) {
        return false;
      }
    }

    return true;
  };

  const filteredFields = fields.filter(isFieldVisible);

  // Render fields grouped by their lead step so a divider can separate each
  // step's fields (e.g. Basic details | Annuitant details) instead of showing
  // one flat list. Single-step edits (stepIndex !== null) yield one group.
  const renderGroups =
    stepIndex !== null
      ? [formDefinition.fieldGroups[stepIndex]].filter(Boolean)
      : formDefinition.fieldGroups;

  const visibleFieldGroups = renderGroups
    .map((group, gIdx) => ({
      key:
        group?.fieldReference || group?.name || group?.title || `group-${gIdx}`,
      fields: (group?.fields || []).filter(isFieldVisible),
    }))
    .filter((group) => group.fields.length > 0);

  const getAnnuitantAgeError = () => {
    const annuitantGroup = isAnnuitantStep(currentStepGroup)
      ? currentStepGroup
      : formDefinition?.fieldGroups?.find((g) => isAnnuitantStep(g));
    if (!annuitantGroup) return null;

    const annuitantRelationshipField =
      (annuitantGroup?.fields || []).find((field) =>
        getFieldToken(field).includes("relationship"),
      ) || relationshipField;
    const annuitantRelationshipFieldName =
      annuitantRelationshipField?.fieldReference ||
      annuitantRelationshipField?.name ||
      annuitantRelationshipField?.fieldName;
    const annuitantDobField = (annuitantGroup?.fields || []).find((field) =>
      isDobField(field),
    );
    const annuitantDobFieldName =
      annuitantDobField?.fieldReference ||
      annuitantDobField?.name ||
      annuitantDobField?.fieldName;

    const annuitantRelationshipValue = resolveRelationshipReference(
      annuitantRelationshipFieldName
        ? editData?.[annuitantRelationshipFieldName]
        : relationshipValue,
    );
    const selfSelected = isSelfRelationshipSelected(annuitantRelationshipValue);
    const primaryLifeAssuredDob =
      resolveCurrentFieldValue("dateOfBirth") ||
      resolveCurrentFieldValue("lifeAssuredDob") ||
      resolveCurrentFieldValue("proposerDob") ||
      "";
    const dobToValidate = selfSelected
      ? primaryLifeAssuredDob
      : annuitantDobFieldName
        ? editData?.[annuitantDobFieldName]
        : "";

    if (!String(dobToValidate || "").trim()) return null;

    const age = calculateAge(dobToValidate);
    if (
      age === null ||
      !isDobWithinAgeRange(dobToValidate, {
        minAge: ANNUITANT_MIN_AGE,
        maxAge: ANNUITANT_MAX_AGE,
        maxAgeMonths: 11,
        maxAgeDays: 30,
      })
    ) {
      const fieldName = selfSelected
        ? annuitantRelationshipFieldName || annuitantDobFieldName
        : annuitantDobFieldName || annuitantRelationshipFieldName;
      if (!fieldName) return null;
      return {
        fieldName,
        message: ANNUITANT_AGE_ERROR,
      };
    }

    return null;
  };

  /**
   * "Other"/"Others" gender primary annuitant must be 30 to 45y364d. For a
   * Self annuitant the proposer's gender and DOB are the annuitant's.
   */
  const getGenderRestrictedAgeError = () => {
    const groups = formDefinition?.fieldGroups || [];
    const annuitantGroup = groups.find((g) => isAnnuitantStep(g));
    if (!annuitantGroup) return null;
    const annuitantFields = annuitantGroup.fields || [];
    const fieldNameOf = (field) =>
      field?.fieldReference || field?.name || field?.fieldName;

    const relationField = annuitantFields.find((field) =>
      getFieldToken(field).includes("relationship"),
    );
    const relationName = fieldNameOf(relationField);
    const relationRaw = relationName ? resolveCurrentFieldValue(relationName) : "";
    const selfOption = (relationField?.options || []).find(
      (opt) => normalize(opt?.label?.en_US ?? opt?.label) === "self",
    );
    const selfSelected =
      normalize(relationRaw) === "self" ||
      (selfOption &&
        [selfOption.reference, selfOption.value].some(
          (v) => v != null && String(v).trim() === String(relationRaw ?? "").trim(),
        ));

    let genderField;
    let gender;
    let dobName;
    let dob;
    if (selfSelected) {
      const proposerFields = groups
        .filter((g) => !isAnnuitantStep(g))
        .flatMap((g) => g.fields || []);
      genderField = proposerFields.find(isGenderField);
      const proposerDobField = proposerFields.find(isDobField);
      dobName = fieldNameOf(proposerDobField);
      gender =
        resolveCurrentFieldValue(fieldNameOf(genderField)) ||
        resolveCurrentFieldValue("gender") ||
        resolveCurrentFieldValue("proposerGender");
      dob =
        resolveCurrentFieldValue(dobName) ||
        resolveCurrentFieldValue("dateOfBirth") ||
        resolveCurrentFieldValue("proposerDob");
    } else {
      genderField = annuitantFields.find(isGenderField);
      const annuitantDobField = annuitantFields.find(isDobField);
      dobName = fieldNameOf(annuitantDobField);
      gender =
        resolveCurrentFieldValue(fieldNameOf(genderField)) ||
        resolveCurrentFieldValue("annuitantGender") ||
        resolveCurrentFieldValue("primaryAnnuitantGender");
      dob =
        resolveCurrentFieldValue(dobName) ||
        resolveCurrentFieldValue("annuitantDateOfBirth") ||
        resolveCurrentFieldValue("primaryAnnuitantDob");
    }

    if (!violatesGenderRestrictedAge(gender, dob, genderField)) return null;

    // Attach the error to a field the user can actually see in this modal.
    const visibleNames = new Set(filteredFields.map(fieldNameOf));
    const candidates = [dobName, fieldNameOf(genderField), relationName].filter(Boolean);
    const fieldName =
      candidates.find((name) => visibleNames.has(name)) || candidates[0];
    return fieldName ? { fieldName, message: GENDER_RESTRICTED_AGE_ERROR } : null;
  };

  const syncAnnuitantSelfFields = (sourceData = {}) => {
    // Keep the generic DOB aliases (proposerDob, dob, lifeAssuredDob) in sync
    // with dateOfBirth whenever it's edited. Downstream quote/premium payload
    // builders (see premiumService.getEffectiveLeadInput) prioritize
    // proposerDob/dob over dateOfBirth, so leaving them stale after an edit
    // makes the new DOB invisible to quote calculation even though the
    // journey record shows the updated dateOfBirth.
    const editedDob = sourceData?.dateOfBirth;
    const base = editedDob
      ? {
          ...sourceData,
          dob: editedDob,
          proposerDob: editedDob,
          lifeAssuredDob: editedDob,
        }
      : sourceData;

    const annuitantGroup = formDefinition?.fieldGroups?.find((g) =>
      isAnnuitantStep(g),
    );
    if (!annuitantGroup) return base;
    const annuitantRelationshipField = (annuitantGroup?.fields || []).find((field) =>
      getFieldToken(field).includes("relationship"),
    );
    const annuitantRelationshipFieldName =
      annuitantRelationshipField?.fieldReference ||
      annuitantRelationshipField?.name ||
      annuitantRelationshipField?.fieldName;
    const annuitantDobField = (annuitantGroup?.fields || []).find((field) =>
      isDobField(field),
    );
    const annuitantDobFieldName =
      annuitantDobField?.fieldReference ||
      annuitantDobField?.name ||
      annuitantDobField?.fieldName;
    const annuitantGenderField = (annuitantGroup?.fields || []).find((field) =>
      isGenderField(field),
    );
    const annuitantGenderFieldName =
      annuitantGenderField?.fieldReference ||
      annuitantGenderField?.name ||
      annuitantGenderField?.fieldName;
    const relationshipValue = resolveRelationshipReference(
      annuitantRelationshipFieldName
        ? base?.[annuitantRelationshipFieldName]
        : undefined,
    );
    if (!isSelfRelationshipSelected(relationshipValue)) return base;

    const primaryDob =
      base?.dateOfBirth ||
      base?.proposerDob ||
      base?.lifeAssuredDob ||
      base?.dob ||
      "";
    const primaryGender =
      base?.proposerGender ||
      base?.gender ||
      base?.lifeAssuredGender ||
      "";

    const next = { ...base };
    if (annuitantDobFieldName && primaryDob) {
      next[annuitantDobFieldName] = primaryDob;
    }
    if (annuitantGenderFieldName && primaryGender) {
      next[annuitantGenderFieldName] = primaryGender;
    }
    // Keep commonly consumed aliases in sync for quote payload builders.
    if (primaryDob) {
      next.annuitantDateOfBirth = primaryDob;
      next.primaryAnnuitantDob = primaryDob;
      next.lifeAssuredDob = primaryDob;
    }
    if (primaryGender) {
      next.annuitantGender = primaryGender;
      next.primaryAnnuitantGender = primaryGender;
      next.lifeAssuredGender = primaryGender;
    }
    return next;
  };

  return (
    <div className={`edit-modal-backdrop ${className}`} onClick={onClose}>
      <div
        ref={modalRef}
        className={`edit-modal-container edit-step-modal ${stepIndex === 0 ? 'basic-edit-modal' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-modal-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="edit-modal-header">
          <h5 className="edit-modal-title light" id="edit-modal-title">
            {title}
          </h5>
          <button
            className="edit-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.781 13.7198C14.8507 13.7895 14.906 13.8722 14.9437 13.9632C14.9814 14.0543 15.0008 14.1519 15.0008 14.2504C15.0008 14.349 14.9814 14.4465 14.9437 14.5376C14.906 14.6286 14.8507 14.7114 14.781 14.781C14.7114 14.8507 14.6286 14.906 14.5376 14.9437C14.4465 14.9814 14.349 15.0008 14.2504 15.0008C14.1519 15.0008 14.0543 14.9814 13.9632 14.9437C13.8722 14.906 13.7895 14.8507 13.7198 14.781L7.50042 8.56073L1.28104 14.781C1.14031 14.9218 0.94944 15.0008 0.750417 15.0008C0.551394 15.0008 0.360523 14.9218 0.219792 14.781C0.0790615 14.6403 3.92322e-09 14.4494 0 14.2504C-3.92322e-09 14.0514 0.0790615 13.8605 0.219792 13.7198L6.4401 7.50042L0.219792 1.28104C0.0790615 1.14031 0 0.94944 0 0.750417C0 0.551394 0.0790615 0.360523 0.219792 0.219792C0.360523 0.0790615 0.551394 0 0.750417 0C0.94944 0 1.14031 0.0790615 1.28104 0.219792L7.50042 6.4401L13.7198 0.219792C13.8605 0.0790615 14.0514 -3.92322e-09 14.2504 0C14.4494 3.92322e-09 14.6403 0.0790615 14.781 0.219792C14.9218 0.360523 15.0008 0.551394 15.0008 0.750417C15.0008 0.94944 14.9218 1.14031 14.781 1.28104L8.56073 7.50042L14.781 13.7198Z"
                fill="#3C3D3D"
              />
            </svg>
          </button>
        </div>
        <div className="edit-modal-contents edit-step-modal-fields">
          {visibleFieldGroups.map((group, groupIndex) => (
            <React.Fragment key={group.key}>
              {group.fields.map((field, i) => {
                const fieldName =
                  field.fieldReference || field.name || field.fieldName;
                return (
                  <DynamicFormField
                    key={fieldName || i}
                    field={field}
                    value={editData[fieldName]}
                    onChange={handleChange}
                    error={errors[fieldName] || null}
                    disabled={disableAllFields || disabledFieldNameSet.has(fieldName)}
                    useDdMmYyyyForDob
                    minAge={fieldName === "annuitantDateOfBirth" ? 30 : 18}
                    maxAge={75}
                  />
                );
              })}
              {groupIndex < visibleFieldGroups.length - 1 && (
                <hr className="edit-step-modal-divider" />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="edit-modal-footer">
          <button
            className="primary-button btn regular "
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : disableAllFields ? 'Okay' : 'Save Changes'}
          </button>
        </div>
      </div>
      <AttentionModal
        isOpen={showAttentionModal}
        onConfirm={confirmAttentionModal}
        onDismiss={dismissAttentionModal}
        type="error"
        btnTitle="OK"
        bottomBtn="Cancel"
      >
        <img src={attentionImg} alt="" width="60" height="56" />
        <h4 className="modal-title">Attention</h4>
        <p>
          You have selected your tax residence outside India. You will be
          required to fill up FATCA/CRS declaration format, sent to your
          registered email ID, and submit it along with your required documents.
          <br />
        </p>
      </AttentionModal>
    </div>
  );
};

export default EditStepModal;
